import React, { useState } from 'react';

const HiringForm: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    status: '',
    experience: '',
    expectedSalary: '',
    noticePeriod: '',
    location: '',
    linkedinProfile: '',
    resume: null as File | null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [debugInfo, setDebugInfo] = useState('');

  // 🚨 REPLACE THIS WITH YOUR NEW APPS SCRIPT URL FROM STEP 2
  const API_URL = 'https://script.google.com/macros/s/AKfycbxULBqnSle0k6IKOmoKKXZ-Yynvxt6GwShn5OM4-vYelwXhNMaBXlwfsHcuE0o20YnONw/exec';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, resume: file }));
    
    if (file) {
      setDebugInfo(`File selected: ${file.name} (${file.type}, ${(file.size/1024/1024).toFixed(2)}MB)`);
    } else {
      setDebugInfo('No file selected');
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result as string;
        const base64Data = base64.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = error => reject(error);
    });
  };

  const validateForm = () => {
    const errors = [];
    
    if (!formData.fullName.trim()) errors.push('Full name is required');
    if (!formData.email.trim()) errors.push('Email is required');
    if (!formData.phone.trim()) errors.push('Phone is required');
    if (!formData.status) errors.push('Status is required');
    if (!formData.experience) errors.push('Experience is required');
    if (!formData.noticePeriod) errors.push('Notice period is required');
    if (!formData.location.trim()) errors.push('Location is required');
    if (!formData.resume) errors.push('Resume is required');
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      errors.push('Please enter a valid email address');
    }
    
    // File validation
    if (formData.resume) {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(formData.resume.type)) {
        errors.push('Please upload a PDF or Word document');
      }
      if (formData.resume.size > 5 * 1024 * 1024) {
        errors.push('File size must be less than 5MB');
      }
    }
    
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setMessage('❌ Please fix these errors:\n' + validationErrors.join('\n'));
      return;
    }
    
    setIsSubmitting(true);
    setMessage('');
    setDebugInfo('');
  
    try {
      console.log('🚀 Starting submission...');
      setDebugInfo('🚀 Starting submission...');
  
      // Create FormData (not JSON)
      const submitData = new FormData();
      
      submitData.append('fullName', formData.fullName);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('status', formData.status);
      submitData.append('experience', formData.experience);
      submitData.append('expectedSalary', formData.expectedSalary);
      submitData.append('noticePeriod', formData.noticePeriod);
      submitData.append('location', formData.location);
      submitData.append('linkedinProfile', formData.linkedinProfile);
      submitData.append('submissionTime', new Date().toISOString());
      
      // Add file directly
      if (formData.resume) {
        submitData.append('resume', formData.resume, formData.resume.name);
        setDebugInfo(prev => prev + '\n📄 File added to FormData');
      }
  
      setDebugInfo(prev => prev + '\n📤 Sending FormData to server...');
  
      // Submit with FormData (no headers needed)
      const response = await fetch(API_URL, {
        method: 'POST',
        body: submitData
        // Don't set Content-Type header - let browser handle it
      });
  
      console.log('📡 Response status:', response.status);
      setDebugInfo(prev => prev + `\n📡 Response: ${response.status}`);
  
      const responseText = await response.text();
      console.log('📄 Response:', responseText);
  
      let responseData: any;
      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ Parse error:', parseError);
        throw new Error('Invalid response from server');
      }
  
      if (response.ok && responseData.success) {
        setMessage(`✅ Success! ${responseData.message}`);
        setDebugInfo(prev => prev + `\n🎉 Success! File uploaded: ${responseData.resumeUploaded ? 'Yes' : 'No'}`);
  
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          status: '',
          experience: '',
          expectedSalary: '',
          noticePeriod: '',
          location: '',
          linkedinProfile: '',
          resume: null
        });
  
        const fileInput = document.getElementById('resume') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
  
      } else {
        throw new Error(responseData.error || 'Submission failed');
      }
  
    } catch (error) {
      console.error('💥 Error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setMessage(`❌ Error: ${errorMessage}`);
      setDebugInfo(prev => prev + `\n💥 Error: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 text-white">
            <h1 className="text-3xl font-bold mb-2">🚀 Join Our Team</h1>
            <p className="text-lg opacity-90">We're looking for talented individuals to help us build the future</p>
          </div>

          <div className="p-8">
            <div className="space-y-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h2 className="text-xl font-semibold text-blue-600 mb-4">👤 Personal Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your email address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your phone number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Location *</label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="City, State"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h2 className="text-xl font-semibold text-blue-600 mb-4">💼 Professional Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Status *</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select your current status</option>
                      <option value="Student">Student</option>
                      <option value="Working Professional">Working Professional</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Experience Level *</label>
                    <select
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select experience level</option>
                      <option value="Fresher (0-1 years)">Fresher (0-1 years)</option>
                      <option value="Junior (1-3 years)">Junior (1-3 years)</option>
                      <option value="Mid-level (3-5 years)">Mid-level (3-5 years)</option>
                      <option value="Senior (5-8 years)">Senior (5-8 years)</option>
                      <option value="Lead (8+ years)">Lead (8+ years)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expected Salary (Optional)</label>
                    <input
                      type="text"
                      name="expectedSalary"
                      value={formData.expectedSalary}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 5-8 LPA or Negotiable"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notice Period *</label>
                    <select
                      name="noticePeriod"
                      value={formData.noticePeriod}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select notice period</option>
                      <option value="Immediate">Immediate</option>
                      <option value="15 days">15 days</option>
                      <option value="30 days">30 days</option>
                      <option value="60 days">60 days</option>
                      <option value="90 days">90 days</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h2 className="text-xl font-semibold text-blue-600 mb-4">🔗 Additional Information</h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn Profile (Optional)</label>
                  <input
                    type="url"
                    name="linkedinProfile"
                    value={formData.linkedinProfile}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h2 className="text-xl font-semibold text-blue-600 mb-4">📄 Resume</h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Resume *</label>
                  <input
                    type="file"
                    id="resume"
                    name="resume"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-sm text-gray-600 mt-1">Accepted formats: PDF, DOC, DOCX (Max size: 5MB)</p>
                  
                  {formData.resume && (
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center space-x-2">
                      <span className="text-blue-600">📄</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-800">{formData.resume.name}</p>
                        <p className="text-xs text-blue-600">
                          {(formData.resume.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <span className="text-green-600">✅</span>
                    </div>
                  )}
                </div>
              </div>

              {message && (
                <div className={`p-4 rounded-lg border ${
                  message.includes('Success') 
                    ? 'bg-green-50 border-green-200 text-green-800' 
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}>
                  <div className="whitespace-pre-wrap">{message}</div>
                </div>
              )}

              <div className="text-center pt-6">
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className={`px-8 py-3 text-lg font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors ${
                    isSubmitting ? 'opacity-75 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting Application...
                    </div>
                  ) : (
                    '🚀 Submit Application'
                  )}
                </button>
              </div>
            </div>

            {debugInfo && (
              <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-md">
                <h3 className="font-medium text-gray-800 mb-2">🔍 Debug Info:</h3>
                <pre className="text-xs text-gray-600 whitespace-pre-wrap">{debugInfo}</pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HiringForm;